// D288: the ranked checkmark — "does a game started from these settings count?"
//
// One component for every place the question is asked (the Find-a-game panel, the lobby), so
// the mark, its words and its ⓘ explanation cannot drift between them. The verdict itself is
// never decided here: the panel passes what its lane PROMISES and the lobby passes the
// server's live `rankVerdict`, so the ui only ever draws an answer it was given.
//
// The ⓘ answers on hover (a pointer) and on tap (a finger, where there is no hover): CSS
// shows the bubble on :hover/:focus-within, and a click toggles `--open` for touch. Every
// string is textContent (T-SEC-006).

export interface RankCheckState {
  ranked: boolean;
  /** Plain words for the ⓘ bubble: why this table does or does not rate. */
  reason: string;
}

export interface RankCheck {
  readonly root: HTMLElement;
  set(next: RankCheckState): void;
}

export function mountRankCheck(doc: Document, initial: RankCheckState): RankCheck {
  const root = doc.createElement('div');
  root.className = 'rank-check';
  root.dataset['rankCheck'] = '1';

  const mark = doc.createElement('span');
  mark.className = 'rank-check-mark';
  mark.setAttribute('aria-hidden', 'true');
  const label = doc.createElement('span');
  label.className = 'rank-check-label';

  const info = doc.createElement('button');
  info.type = 'button';
  info.className = 'rank-check-info';
  info.dataset['rankCheckInfo'] = '1';
  info.textContent = 'i';
  info.setAttribute('aria-expanded', 'false');

  const bubble = doc.createElement('span');
  bubble.className = 'rank-check-bubble';
  bubble.setAttribute('role', 'tooltip');
  bubble.dataset['rankCheckReason'] = '1';
  const bubbleId = `rank-check-${Math.random().toString(36).slice(2, 9)}`;
  bubble.id = bubbleId;
  info.setAttribute('aria-describedby', bubbleId);

  info.addEventListener('click', (e) => {
    e.stopPropagation(); // the play panel's scrim must not read this tap as "close"
    const open = !root.classList.contains('rank-check--open');
    root.classList.toggle('rank-check--open', open);
    info.setAttribute('aria-expanded', open ? 'true' : 'false');
  });

  root.append(mark, label, info, bubble);

  const set = (s: RankCheckState): void => {
    root.dataset['rankCheck'] = s.ranked ? 'ranked' : 'unranked';
    root.classList.toggle('rank-check--ranked', s.ranked);
    mark.textContent = s.ranked ? '✓' : '✕';
    label.textContent = s.ranked ? 'Ranked' : 'Unranked';
    info.setAttribute('aria-label', s.ranked ? 'Why is this game ranked?' : 'Why is this game unranked?');
    bubble.textContent = s.reason;
  };
  set(initial);
  return { root, set };
}
